import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LineChart as LineIcon, BarChart3, AreaChart as AreaIcon, PieChart as PieIcon } from "lucide-react";
import { formatRupiah } from "@/lib/api";

const TYPES = [
  { value: "bar",  label: "Batang", icon: BarChart3 },
  { value: "line", label: "Garis",  icon: LineIcon },
  { value: "area", label: "Area",   icon: AreaIcon },
];

const COLORS = {
  sph: "#0ea5e9",      // sky-500
  invoice: "#f59e0b",  // amber-500
  total: "#6366f1",    // indigo-500
  grid: "#e2e8f0",
  axis: "#64748b",
};

const STATUS_COLORS = {
  Pengajuan: "#f59e0b",
  Selesai: "#10b981",
  Batal: "#f43f5e",
};

function fmtShort(n) {
  if (!n) return "0";
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "jt";
  if (n >= 1_000) return Math.round(n / 1_000) + "rb";
  return n.toString();
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-white/95 px-3 py-2 shadow-md backdrop-blur" data-testid="chart-tooltip">
      <div className="mb-1 text-xs font-medium text-ink-900">{label}</div>
      {payload.map((p) => (
        <div key={p.name || p.dataKey} className="flex items-center gap-2 text-xs">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-ink-500">{p.name}:</span>
          <span className="font-mono font-semibold text-ink-900">
            {typeof p.value === "number" && p.value > 999 ? formatRupiah(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function StatsCharts({ rows, statusBreakdown }) {
  const [type, setType] = useState("bar");
  const [metric, setMetric] = useState("nominal"); // "nominal" | "count"

  const chartData = useMemo(() => {
    return rows.map((r) => ({
      date: r.date?.slice(5) ?? "",
      fullDate: r.date,
      SPH: metric === "nominal" ? r.nominal_sph : r.sph,
      Invoice: metric === "nominal" ? r.nominal_invoice : r.invoice,
      Total: metric === "nominal" ? r.nominal : r.total,
    }));
  }, [rows, metric]);

  const pieData = useMemo(() => {
    const total = rows.reduce(
      (a, r) => ({
        sph: a.sph + (metric === "nominal" ? r.nominal_sph : r.sph),
        invoice: a.invoice + (metric === "nominal" ? r.nominal_invoice : r.invoice),
      }),
      { sph: 0, invoice: 0 }
    );
    return [
      { name: "SPH", value: total.sph, color: COLORS.sph },
      { name: "Invoice", value: total.invoice, color: COLORS.invoice },
    ].filter((d) => d.value > 0);
  }, [rows, metric]);

  const statusData = useMemo(() => {
    if (!statusBreakdown) return [];
    return [
      { name: "Pengajuan", value: statusBreakdown.pengajuan || 0, color: STATUS_COLORS.Pengajuan },
      { name: "Selesai",   value: statusBreakdown.selesai   || 0, color: STATUS_COLORS.Selesai },
      { name: "Batal",     value: statusBreakdown.batal     || 0, color: STATUS_COLORS.Batal },
    ].filter((d) => d.value > 0);
  }, [statusBreakdown]);

  const axisProps = {
    tick: { fontSize: 11, fill: COLORS.axis },
    tickLine: false,
    axisLine: { stroke: COLORS.grid },
  };

  const renderMain = () => {
    if (type === "line") {
      return (
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="date" {...axisProps} />
          <YAxis tickFormatter={fmtShort} {...axisProps} />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: COLORS.grid }} />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
          <Line type="monotone" dataKey="SPH"     stroke={COLORS.sph}     strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="Invoice" stroke={COLORS.invoice} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
        </LineChart>
      );
    }
    if (type === "area") {
      return (
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gSph" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.sph} stopOpacity={0.55} />
              <stop offset="100%" stopColor={COLORS.sph} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="gInv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.invoice} stopOpacity={0.55} />
              <stop offset="100%" stopColor={COLORS.invoice} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="date" {...axisProps} />
          <YAxis tickFormatter={fmtShort} {...axisProps} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
          <Area type="monotone" dataKey="SPH"     stroke={COLORS.sph}     strokeWidth={2} fill="url(#gSph)" />
          <Area type="monotone" dataKey="Invoice" stroke={COLORS.invoice} strokeWidth={2} fill="url(#gInv)" />
        </AreaChart>
      );
    }
    // bar (default, stacked)
    return (
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} barCategoryGap={4}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
        <XAxis dataKey="date" {...axisProps} />
        <YAxis tickFormatter={fmtShort} {...axisProps} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(99,102,241,0.05)" }} />
        <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
        <Bar dataKey="SPH"     stackId="a" fill={COLORS.sph}     radius={[0, 0, 0, 0]} />
        <Bar dataKey="Invoice" stackId="a" fill={COLORS.invoice} radius={[4, 4, 0, 0]} />
      </BarChart>
    );
  };

  return (
    <div className="space-y-4">
      {/* Chart toolbar */}
      <Card className="p-4 border-border shadow-none">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="overline">Visualisasi Trend Harian</p>
            <p className="mt-0.5 text-xs text-ink-500">SPH vs Invoice — pilih jenis diagram & metrik.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Chart Type */}
            <div className="flex items-center gap-1 rounded-full border border-border bg-card p-1 shadow-sm">
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.value;
                return (
                  <Button
                    key={t.value}
                    size="sm"
                    variant={active ? "default" : "ghost"}
                    onClick={() => setType(t.value)}
                    className={`h-8 rounded-full px-3 text-xs ${active ? "bg-ink-900 text-white hover:bg-ink-900" : "text-ink-500 hover:bg-secondary"}`}
                    data-testid={`chart-type-${t.value}`}
                  >
                    <Icon className="mr-1 h-3.5 w-3.5" /> {t.label}
                  </Button>
                );
              })}
            </div>
            {/* Metric */}
            <div className="flex items-center gap-1 rounded-full border border-border bg-card p-1 shadow-sm">
              <Button size="sm" variant={metric==="nominal"?"default":"ghost"} onClick={()=>setMetric("nominal")} className={`h-8 rounded-full px-3 text-xs ${metric==="nominal"?"bg-brand text-white hover:bg-brand-600":"text-ink-500 hover:bg-secondary"}`} data-testid="chart-metric-nominal">Rupiah</Button>
              <Button size="sm" variant={metric==="count"?"default":"ghost"} onClick={()=>setMetric("count")} className={`h-8 rounded-full px-3 text-xs ${metric==="count"?"bg-brand text-white hover:bg-brand-600":"text-ink-500 hover:bg-secondary"}`} data-testid="chart-metric-count">Jumlah</Button>
            </div>
          </div>
        </div>

        <div className="mt-4 h-72 w-full" data-testid="trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            {renderMain()}
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Donut charts grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* SPH vs Invoice */}
        <Card className="p-5 border-border shadow-none">
          <p className="overline mb-1 flex items-center gap-1.5"><PieIcon className="h-3 w-3" /> Komposisi SPH vs Invoice</p>
          <p className="text-xs text-ink-500">Total {metric === "nominal" ? "nominal" : "jumlah"} pada rentang ini.</p>
          <div className="mt-4 h-64" data-testid="composition-chart">
            {pieData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-ink-500">Belum ada data.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                    {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Status distribution */}
        <Card className="p-5 border-border shadow-none">
          <p className="overline mb-1 flex items-center gap-1.5"><PieIcon className="h-3 w-3" /> Distribusi Status Surat</p>
          <p className="text-xs text-ink-500">Pengajuan / Selesai / Batal — periode aktif.</p>
          <div className="mt-4 h-64" data-testid="status-chart">
            {statusData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-ink-500">Belum ada data.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                    {statusData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
